import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import {
    IChannelHistoryParams,
    IChannelHistoryEntity,
    ISeriesHistoryParams,
    ISeriesHistoryResponse,
    GetKidHistoryResponse,
} from '../../entity/interface/iChannelSeriesHistory';
import { IGetChannelHistoryUseCase } from '../../core/usecases/channelSeriesHistory/GetChannelHistoryUseCase';
import { IGetProfileWatchesHistoryUseCase } from '../../core/usecases/channelSeriesHistory/GetProfileWatchesHistoryUseCase';
import { IGetSeriesHistoryUseCase } from '../../core/usecases/channelSeriesHistory/GetSeriesHistoryUseCase';

export interface IChannelSeriesHistoryService {
    getChannelHistory(
        apiKey: string,
        profileName: string,
        paylod: IChannelHistoryParams,
        token: string,
        identityToken: string
    ): Promise<IChannelHistoryEntity[]>;

    getSeriesHistory(
        apiKey: string,
        profileName: string,
        paylod: ISeriesHistoryParams,
        token: string,
        identityToken: string
    ): Promise<ISeriesHistoryResponse>;

    getProfileWatchesHistory(
        apiKey: string,
        profileName: string,
        userName: string,
        pageNumber: number,
        pageSize: number,
        identityToken: string
    ): Promise<GetKidHistoryResponse>;
}

export class ChannelSeriesHistoryService implements IChannelSeriesHistoryService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get channelHistoryUseCase(): IGetChannelHistoryUseCase {
        return container.resolve<IGetChannelHistoryUseCase>(DI.USECASES.IGetChannelHistoryUseCase);
    }

    private get profileWatchesHistoryUseCase(): IGetProfileWatchesHistoryUseCase {
        return container.resolve<IGetProfileWatchesHistoryUseCase>(DI.USECASES.IGetProfileWatchesHistoryUseCase);
    }

    private get seriesHistoryUseCase(): IGetSeriesHistoryUseCase {
        return container.resolve<IGetSeriesHistoryUseCase>(DI.USECASES.IGetSeriesHistoryUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    /**
     * Get the channel history list.
     * @param {string} apiKey
     * @param {string} profileName
     * @param {IChannelHistoryParams} params The allowed params to filter the list.
     * @param {string} token
     * @param {string} identityToken
     * @returns {IBookMarkResponse} List of bookmark items.
     */
    async getChannelHistory(
        apiKey: string,
        profileName: string,
        payload: IChannelHistoryParams,
        token: string,
        identityToken: string
    ): Promise<IChannelHistoryEntity[]> {
        return await this.channelHistoryUseCase.execute(apiKey, profileName, payload, token, identityToken);
    }

    /**
     * Get the Series history list.
     * @param {string} apiKey
     * @param {string} profileName
     * @param {ISeriesHistoryParams} params The allowed params to filter the list.
     * @param {string} token
     * @param {string} identityToken
     * @returns {IBookMarkResponse} List of bookmark items.
     */
    async getSeriesHistory(
        apiKey: string,
        profileName: string,
        payload: ISeriesHistoryParams,
        token: string,
        identityToken: string
    ): Promise<ISeriesHistoryResponse> {
        return await this.seriesHistoryUseCase.execute(apiKey, profileName, payload, token, identityToken);
    }

    /**
     * Kids History.
     * @param {string} apiKey
     * @param {string} userName
     * @param {string} profileName
     * @param {number} pageNumber
     * @param {number} pageSize
     * @param {string} identityToken
     * @returns {Promise<GetKidHistoryResponse>} A promise that resolves to an object containing an array of viewerShip.
     */
    async getProfileWatchesHistory(
        apiKey: string,
        profileName: string,
        userName: string,
        pageNumber: number,
        pageSize: number,
        identityToken: string
    ): Promise<GetKidHistoryResponse> {
        return await this.profileWatchesHistoryUseCase.execute(
            apiKey,
            profileName,
            userName,
            pageNumber,
            pageSize,
            identityToken
        );
    }
}
